"use client";

import { LargeAgentCard } from "@/components/agent/large-agent-card";
import { ListingAgentCard } from "@/components/agent/listing-agent-card";
import Image from "@/components/common/image";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { useCurrentUser } from "@/hooks/use-current-user";
import { api } from "@/lib/api-client";
import { valuesWithAppName } from "@/lib/i18n";
import type { DetailedListing, DetailedListingViewAs } from "@/lib/types";
import { buildStepUrl, shortName } from "@repo/utils";
import { LoaderCircleIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { useSearchParams } from "next/navigation";

import agentWave from "./assets/agent-wave.svg";

export type ListingAgentProps = {
  listing: DetailedListing;
};

/**
 * Rendered once, right under the gallery. Buyers get a single "Listed by"
 * row; the owner gets the broker card or the "find a broker" prompt.
 */
export function ListingAgent({ listing }: ListingAgentProps) {
  const t = useTranslations();

  const { user } = useCurrentUser();
  const searchParams = useSearchParams();

  const viewAs = searchParams.get("viewAs") as DetailedListingViewAs | null;

  const isOwner =
    user && (user.id === listing.ownerId || user.id === listing.sellerAgentId);

  const { data, isLoading } = api.listings.getListingAgent.useQuery(
    { id: listing.id },
    { retry: false, refetchOnWindowFocus: false },
  );

  const buyerView =
    user?.isAgent ||
    !isOwner ||
    (isOwner && (viewAs === "PUBLIC_BUYER" || viewAs === "SECURED_BUYER"));

  if (buyerView) {
    if (isLoading || !data?.agent) {
      return null;
    }

    return (
      <div className="border-b border-border py-6">
        <ListingAgentCard agent={data.agent} />
      </div>
    );
  }

  return (
    <section className="flex flex-col gap-4 border-b border-border py-8 lg:py-10">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-xl font-semibold tracking-tight text-foreground lg:text-[22px]">
          {t("saas.listing.agent.title")}
        </h2>

        {!isLoading && data?.agent && data?.conversation && (
          <Button variant="outline" asChild>
            <Link href={`/chat/${data.conversation.id}`}>
              <Icon icon="MessageSquare" />
              {t(
                data.agent.id === user?.id
                  ? "saas.listing.agent.messageSeller"
                  : "saas.listing.agent.messageAgent",
                { name: shortName(data.agent.name) },
              )}
            </Link>
          </Button>
        )}
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-10">
          <LoaderCircleIcon className="size-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {!isLoading && data?.agent && (
        <LargeAgentCard agent={data.agent} displayBio={false} />
      )}

      {!isLoading && !data?.agent && (
        <div className="flex flex-col gap-4 rounded-2xl border border-dashed border-border bg-muted/40 p-6 md:flex-row md:items-center md:gap-6">
          <Image src={agentWave} alt="" />

          <div className="flex flex-1 flex-col gap-1">
            <p className="text-lg font-semibold text-foreground">
              {t("saas.listing.agent.upsell.title", valuesWithAppName(t, {}))}
            </p>
            <p className="text-sm text-muted-foreground">
              {t("saas.listing.agent.upsell.description1")}
            </p>
            <p className="text-sm text-muted-foreground">
              {t("saas.listing.agent.upsell.description2")}
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <Button asChild>
              <Link href={buildStepUrl(listing, "AGENT_RECOMMENDED")}>
                <Icon icon="Search" />
                {t("saas.listing.agent.upsell.cta")}
              </Link>
            </Button>
            <Button variant="outline" asChild>
              <Link href="/sell-your-business">
                {t("saas.listing.agent.upsell.learnMore")}
              </Link>
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

ListingAgent.displayName = "ListingAgent";
